// Imports => React
import { useMemo, useState } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

// Imports => Constants
import {
  AcIcon,
  AcSanitize,
  AcTextInput,
  ICONS,
  KEYS,
  THEMES,
  TYPES,
} from "@opp/ui-kit";

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcRichContent,
  AcButton,
} from "@opp/ui-kit";
import { withTranslation } from "react-i18next";
import { useFormActions } from "../../hooks";

const _CLASSES = {
  MAIN: "ac-delete-account-modal ac-modal--size-lg",
  CONTENT: "ac-delete-account-modal__content",
};

const AcDeleteAccountModal = ({ store: { ui, user, auth }, data, t, i18n }) => {
  const handleClose = (event) => {
    if (event && event.preventDefault) event.preventDefault();

    ui.reset(KEYS.MODAL, false);
  };

  const [fields, setFields] = useState({
    confirm: "",
  });

  const { handleInputChange } = useFormActions({
    t,
    fields,
    setFields,
  });

  const getContentClassNames = useMemo(() => {
    return clsx([_CLASSES.CONTENT]);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx([_CLASSES.MAIN]);
  }, []);

  const getCloseButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.OMEGA,
      title: `${t("close", "Close", { ns: "common" })}`,
      callback: handleClose,
    };
  }, [handleClose]);

  const getConfirmInputOptions = useMemo(() => {
    return {
      type: TYPES.TEXT,
      label: "",
      name: "confirm",
      value: fields.confirm,
      callback: handleInputChange,
      required: true,
      tabIndex: 1,
      focus: false,
    };
  }, [fields, fields.confirm]);

  const isConfirmed = useMemo(() => {
    let value = fields.confirm.toLowerCase();
    let expected = t("delete-account.confirm-delete-word", {
      ns: "account",
    }).toLowerCase();

    return value === expected || value === AcSanitize(expected);
  }, [fields, fields.confirm, i18n.resolvedLanguage]);

  const handleDeleteMerchantAccount = () => {
    user.delete(t).then((response) => {
      handleClose();
      // CASE 200 success -> log out
      // CASE 301 partly due to financial data, but users are delete so -> log out
      //
      // CASE 300 open transactions, users not deleted -> stay logged in
      // CASE 400 error, have to investigate core logs, users not deleted -> stay logged in
      if (response.status_code === 200 || response.status_code === 301) {
        auth.logout(t);
      }
    });
  };

  return (
    <div className={getStyleClassNames}>
      <div className={getContentClassNames}>
        <AcContainer fluid>
          <AcRow>
            <AcColumn>
              <AcRichContent
                content={`<div class='h-text--color-medium'>${t(
                  "delete-account.intro",
                  { ns: "account" }
                )}</div>`}
              />
            </AcColumn>
          </AcRow>

          <AcRow className={"h-margin-y-20"}>
            <AcColumn>
              <AcRichContent
                content={`<p class='h-text--color-pitch'>${t(
                  "delete-account.confirm-delete-text",
                  {
                    deleteWord: `${t("delete-account.confirm-delete-word", {
                      ns: "account",
                    })}`,
                    ns: "account",
                  }
                )}</p>`}
              />
            </AcColumn>
          </AcRow>

          <AcRow>
            <AcColumn xs={12} sm={12} md={6}>
              <AcTextInput {...getConfirmInputOptions} />
            </AcColumn>
          </AcRow>

          <AcRow className={"h-margin-top-20"}>
            <AcColumn className={"h-text--align-left h-flex-v-align-center"}>
              <AcButton {...getCloseButtonOptions}>
                <span>
                  {t("delete-account.close", "Cancel my request", {
                    ns: "account",
                  })}
                </span>
              </AcButton>
            </AcColumn>

            <AcColumn className={"h-text--align-right"}>
              <AcButton
                theme={THEMES.ERROR}
                type={TYPES.BUTTON}
                title={t(
                  "delete-account.confirm",
                  "Continue to delete my data",
                  {
                    ns: "account",
                  }
                )}
                callback={handleDeleteMerchantAccount}
                disabled={!isConfirmed}
              >
                <span>
                  {t("delete-account.confirm", "Continue to delete my data", {
                    ns: "account",
                  })}
                </span>
                <AcIcon icon={ICONS.DELETE_EMPTY_OUTLINE} />
              </AcButton>
            </AcColumn>
          </AcRow>
        </AcContainer>
      </div>
    </div>
  );
};

export default withTranslation(["common", "store"])(
  withStore(observer(AcDeleteAccountModal))
);
